/**
 * acp-schemas.ts
 *
 * Zod schemas for ACP WebSocket wire messages.
 * Source of truth for runtime validation of all ACP DownstreamMessage and
 * UpstreamMessage frames. Types in lib/transport/src/session-bus.ts are
 * derived from these schemas.
 *
 * Two directed channels per session:
 *   Downstream  ACP → Backend  prompts, cancels, config, round-trip responses
 *   Upstream    Backend → ACP  stream chunks, stop, round-trip requests
 *
 * All session-scoped messages carry sessionId (UuidField).
 * Correlated round-trips carry correlationId (UuidField).
 */

import { z } from "zod";
import { UuidField } from "./common";
import { schemas as messageSchemas } from "./message-structure-schemas";

// ─── Shared primitives ────────────────────────────────────────────────────────

const SessionId = UuidField;
// CorrelationId is an opaque string — not necessarily a UUID.
// Harness call IDs use format "call_<alphanumeric>" which is not UUID.
const CorrelationId = z.string().min(1);

// ACP stop reasons (mirrors acp.StopReason)
const StopReason = z.enum([
  "end_turn",
  "max_tokens",
  "max_turn_requests",
  "refusal",
  "cancelled",
]);

// ─── Inline ACP payload shapes ────────────────────────────────────────────────
// These mirror the ACP SDK types without importing the SDK into the spec lib.
// Kept intentionally loose (passthrough) for forward-compatibility — the SDK
// is the authoritative source for their internal structure. We validate the
// envelope (type + sessionId/correlationId) strictly; payloads are passthrough.

const AcpPromptContent = z.array(z.looseObject({ type: z.string() }));

const AcpSessionUpdate = z.looseObject({
  sessionUpdate: z.string(),
});

const AcpRequestPermissionReq = z.looseObject({
  toolCall: z.looseObject({}),
  options: z.array(z.looseObject({})),
});

const AcpRequestPermissionResult = z.looseObject({});

const AcpReadFileReq = z.looseObject({ path: z.string() });
const AcpReadFileResult = z.looseObject({});

const AcpWriteFileReq = z.looseObject({ path: z.string(), content: z.string() });
const AcpWriteFileResult = z.looseObject({});

const AcpCreateTerminalReq = z.looseObject({ command: z.string() });

const AcpTerminalOutputResult = z.looseObject({});

const AcpTerminalExitResult = z.looseObject({});
const AcpTerminalKillResult = z.looseObject({});

const AcpSetModeParams = z.looseObject({
  sessionId: SessionId,
  modeId: z.string(),
});

const AcpSetModeResult = z.looseObject({});

const AcpSetConfigParams = z.looseObject({
  sessionId: SessionId,
  configId: z.string(),
  value: z.string(),
});

const AcpSetConfigResult = z.looseObject({
  configOptions: z.array(z.looseObject({})),
});

const AcpListSessionsParams = z.looseObject({});

const AcpListSessionsResult = z.looseObject({
  sessions: z.array(z.looseObject({})),
});

const AcpNewSessionParams = z.looseObject({
  cwd: z.string(),
  mcpServers: z.array(z.looseObject({})),
});

const AcpClientCapabilities = z.looseObject({});

const AcpSessionInitState = z.object({
  modes: z.looseObject({}).optional(),
  configOptions: z.array(z.looseObject({})).optional(),
});

// ─── Downstream: ACP → Backend ───────────────────────────────────────────────

// Session lifecycle — sent once per connection before any other downstream messages
export const AcpOpenSessionMessage = z.object({
  type: z.literal("open_session"),
  sessionId: SessionId,
  params: AcpNewSessionParams,
  clientCapabilities: AcpClientCapabilities,
});

export const AcpLoadSessionMessage = z.object({
  type: z.literal("load_session"),
  sessionId: SessionId,
  params: z.looseObject({
    cwd: z.string(),
    mcpServers: z.array(z.looseObject({})),
  }),
  clientCapabilities: AcpClientCapabilities,
});

// Turn lifecycle
export const AcpPromptMessage = z.object({
  type: z.literal("prompt"),
  sessionId: SessionId,
  params: z.looseObject({
    sessionId: SessionId,
    prompt: AcpPromptContent,
  }),
  // Typed tag references from agent-side media upload/tagging — populated before send
  references: messageSchemas.ReferencesMetadata.optional(),
});

// prompt_ready: agent has uploaded the MDC to the messages bucket.
// Backend fetches and processes the MDC from storage using messageId.
export const AcpPromptReadyMessage = z.object({
  type: z.literal("prompt_ready"),
  sessionId: SessionId,
  messageId: UuidField,
});

export const AcpCancelMessage = z.object({
  type: z.literal("cancel"),
  sessionId: SessionId,
});

// Session config (editor-initiated)
export const AcpSetModeMessage = z.object({
  type: z.literal("set_mode"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  params: AcpSetModeParams,
});

export const AcpSetConfigMessage = z.object({
  type: z.literal("set_config"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  params: AcpSetConfigParams,
});

// Session listing (correlated)
export const AcpListSessionsMessage = z.object({
  type: z.literal("list_sessions"),
  correlationId: CorrelationId,
  params: AcpListSessionsParams,
});

// Round-trip responses (ACP resolved a backend-initiated request)
export const AcpPermissionResponseMessage = z.object({
  type: z.literal("permission_response"),
  correlationId: CorrelationId,
  result: AcpRequestPermissionResult,
});

export const AcpFsReadResponseMessage = z.object({
  type: z.literal("fs_read_response"),
  correlationId: CorrelationId,
  result: AcpReadFileResult,
});

export const AcpFsWriteResponseMessage = z.object({
  type: z.literal("fs_write_response"),
  correlationId: CorrelationId,
  result: AcpWriteFileResult,
});

export const AcpTerminalCreateResponseMessage = z.object({
  type: z.literal("terminal_create_response"),
  correlationId: CorrelationId,
  terminalId: z.string().optional(),
});

export const AcpTerminalOutputResponseMessage = z.object({
  type: z.literal("terminal_output_response"),
  correlationId: CorrelationId,
  result: AcpTerminalOutputResult,
});

export const AcpTerminalExitResponseMessage = z.object({
  type: z.literal("terminal_exit_response"),
  correlationId: CorrelationId,
  result: AcpTerminalExitResult,
});

export const AcpTerminalKillResponseMessage = z.object({
  type: z.literal("terminal_kill_response"),
  correlationId: CorrelationId,
  result: AcpTerminalKillResult,
});

export const AcpTerminalReleaseResponseMessage = z.object({
  type: z.literal("terminal_release_response"),
  correlationId: CorrelationId,
});

// Discriminated union of all downstream message types
export const AcpHeartbeatMessage = z.object({
  type: z.literal("heartbeat"),
  participantId: z.string().min(1),
});

export const AcpCustomToolResponseMessage = z.object({
  type: z.literal("custom_tool_response"),
  correlationId: CorrelationId,
  result: z.record(z.string(), z.unknown()),
  isError: z.boolean(),
  errorMessage: z.string().optional(),
});

export const AcpDownstreamMessage = z.discriminatedUnion("type", [
  AcpOpenSessionMessage,
  AcpLoadSessionMessage,
  AcpPromptMessage,
  AcpPromptReadyMessage,
  AcpCancelMessage,
  AcpSetModeMessage,
  AcpSetConfigMessage,
  AcpListSessionsMessage,
  AcpPermissionResponseMessage,
  AcpFsReadResponseMessage,
  AcpFsWriteResponseMessage,
  AcpTerminalCreateResponseMessage,
  AcpTerminalOutputResponseMessage,
  AcpTerminalExitResponseMessage,
  AcpTerminalKillResponseMessage,
  AcpTerminalReleaseResponseMessage,
  AcpHeartbeatMessage,
  AcpCustomToolResponseMessage,
]);

export type AcpDownstreamMessageType = z.infer<typeof AcpDownstreamMessage>;

// ─── Upstream: Backend → ACP ─────────────────────────────────────────────────

// Session lifecycle responses
export const AcpSessionOpenedMessage = z.object({
  type: z.literal("session_opened"),
  sessionId: SessionId,
  result: AcpSessionInitState,
});

export const AcpSessionOpenFailedMessage = z.object({
  type: z.literal("session_open_failed"),
  sessionId: SessionId,
  error: z.object({
    code: z.number(),
    message: z.string(),
  }),
});

export const AcpLoadSessionResponseMessage = z.object({
  type: z.literal("load_session_response"),
  sessionId: SessionId,
  result: z.looseObject({
    configOptions: z.array(z.looseObject({})).optional(),
    modes: z.looseObject({}).optional(),
  }),
});

// Streaming session updates (chunks, tool calls, plan, mode, config, commands)
export const AcpUpdateMessage = z.object({
  type: z.literal("update"),
  sessionId: SessionId,
  update: AcpSessionUpdate,
});

// Turn complete
export const AcpStopMessage = z.object({
  type: z.literal("stop"),
  sessionId: SessionId,
  stopReason: StopReason,
});

// Round-trip requests (backend needs a result from the editor)
export const AcpPermissionRequestMessage = z.object({
  type: z.literal("permission_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  req: AcpRequestPermissionReq,
});

export const AcpFsReadRequestMessage = z.object({
  type: z.literal("fs_read_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  req: AcpReadFileReq,
});

export const AcpFsWriteRequestMessage = z.object({
  type: z.literal("fs_write_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  req: AcpWriteFileReq,
});

export const AcpTerminalCreateRequestMessage = z.object({
  type: z.literal("terminal_create_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  req: AcpCreateTerminalReq,
});

export const AcpTerminalOutputRequestMessage = z.object({
  type: z.literal("terminal_output_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  terminalId: z.string(),
});

export const AcpTerminalExitRequestMessage = z.object({
  type: z.literal("terminal_exit_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  terminalId: z.string(),
});

export const AcpTerminalKillRequestMessage = z.object({
  type: z.literal("terminal_kill_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  terminalId: z.string(),
});

export const AcpTerminalReleaseRequestMessage = z.object({
  type: z.literal("terminal_release_request"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  terminalId: z.string(),
});

// Correlated responses to ACP-initiated requests
export const AcpSetModeResponseMessage = z.object({
  type: z.literal("set_mode_response"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  result: AcpSetModeResult,
});

export const AcpSetConfigResponseMessage = z.object({
  type: z.literal("set_config_response"),
  sessionId: SessionId,
  correlationId: CorrelationId,
  result: AcpSetConfigResult,
});

export const AcpListSessionsResponseMessage = z.object({
  type: z.literal("list_sessions_response"),
  correlationId: CorrelationId,
  result: AcpListSessionsResult,
});

// Discriminated union of all upstream message types
export const AcpUpstreamMessage = z.discriminatedUnion("type", [
  AcpSessionOpenedMessage,
  AcpSessionOpenFailedMessage,
  AcpLoadSessionResponseMessage,
  AcpUpdateMessage,
  AcpStopMessage,
  AcpPermissionRequestMessage,
  AcpFsReadRequestMessage,
  AcpFsWriteRequestMessage,
  AcpTerminalCreateRequestMessage,
  AcpTerminalOutputRequestMessage,
  AcpTerminalExitRequestMessage,
  AcpTerminalKillRequestMessage,
  AcpTerminalReleaseRequestMessage,
  AcpSetModeResponseMessage,
  AcpSetConfigResponseMessage,
  AcpListSessionsResponseMessage,
]);

export type AcpUpstreamMessageType = z.infer<typeof AcpUpstreamMessage>;

// ─── Exports ──────────────────────────────────────────────────────────────────

export const acpSchemas = {
  // Downstream
  AcpDownstreamMessage,
  AcpOpenSessionMessage,
  AcpLoadSessionMessage,
  AcpPromptMessage,
  AcpPromptReadyMessage,
  AcpCancelMessage,
  AcpSetModeMessage,
  AcpSetConfigMessage,
  AcpListSessionsMessage,
  AcpPermissionResponseMessage,
  AcpFsReadResponseMessage,
  AcpFsWriteResponseMessage,
  AcpTerminalCreateResponseMessage,
  AcpTerminalOutputResponseMessage,
  AcpTerminalExitResponseMessage,
  AcpTerminalKillResponseMessage,
  AcpTerminalReleaseResponseMessage,
  AcpHeartbeatMessage,
  AcpCustomToolResponseMessage,
  // Upstream
  AcpUpstreamMessage,
  AcpSessionOpenedMessage,
  AcpSessionOpenFailedMessage,
  AcpLoadSessionResponseMessage,
  AcpUpdateMessage,
  AcpStopMessage,
  AcpPermissionRequestMessage,
  AcpFsReadRequestMessage,
  AcpFsWriteRequestMessage,
  AcpTerminalCreateRequestMessage,
  AcpTerminalOutputRequestMessage,
  AcpTerminalExitRequestMessage,
  AcpTerminalKillRequestMessage,
  AcpTerminalReleaseRequestMessage,
  AcpSetModeResponseMessage,
  AcpSetConfigResponseMessage,
  AcpListSessionsResponseMessage,
};
